'use client'

import { Text } from '@components/Helpers'
import {
  Checkbox,
  FormControlLabel,
  styled,
} from '@mui/material'
import colors from '@theme/theme-dark/base/colors'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'

const StyledCheckbox = styled(Checkbox)({
  '& .MuiSvgIcon-root': {
    // eslint-disable-next-line quotes
    backgroundImage: `url('data:image/svg+xml,<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M0.610291 12.7501C0.610291 6.18817 5.92981 0.868652 12.4918 0.868652C19.0537 0.868652 24.3732 6.18817 24.3732 12.7501C24.3732 19.3121 19.0537 24.6316 12.4918 24.6316C5.92981 24.6316 0.610291 19.3121 0.610291 12.7501Z" fill="%23262626" /> </svg>')`,
    borderColor: colors.transparent.main,
  },
  '&.Mui-checked': {
    color: 'var(--dewi_colors_gray_0)',
    '& .MuiSvgIcon-root': {
      // eslint-disable-next-line quotes
      backgroundImage: `url('data:image/svg+xml,<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M0.0844116 12.7501C0.0844116 6.18817 5.40393 0.868652 11.9659 0.868652C18.5278 0.868652 23.8474 6.18817 23.8474 12.7501C23.8474 19.3121 18.5278 24.6316 11.9659 24.6316C5.40393 24.6316 0.0844116 19.3121 0.0844116 12.7501Z" fill="%23262626" /> <path d="M16.1146 9.30326C16.3099 9.108 16.6265 9.108 16.8217 9.30326L17.5739 10.0554C17.7691 10.2507 17.7691 10.5672 17.5739 10.7625L10.9137 17.4227C10.7184 17.618 10.4018 17.618 10.2066 17.4227L6.16642 13.3826C5.97116 13.1873 5.97116 12.8707 6.16642 12.6755L6.91856 11.9233C7.11382 11.7281 7.4304 11.7281 7.62567 11.9233L9.49946 13.7971C10.0852 14.3829 11.035 14.3829 11.6208 13.7971L16.1146 9.30326Z" fill="white" stroke="white" /> </svg>')`,
      borderColor: colors.transparent.main,
    },
  },
})

const StyledFormControlLabel = ({
  sx,
  control,
  label,
}: {
  sx?: any
  control: JSX.Element
  label: JSX.Element
}) => {
  const FormControl = styled(FormControlLabel)({
    display: 'flex',
    paddingRight: '1rem',
    textTransform: 'capitalize',
    // The legendary toggle is a pill like every other control in the bar:
    // translucent fill, no outline.
    borderRadius: 'var(--dewi_radius_rounded)',
    border: 'none',
    backgroundColor: 'var(--dewi_pill_secondary)',
    ...sx,
  })

  return <FormControl control={control} label={label} />
}

type CheckboxInputProp = {
  label: string
  onChange: (value: boolean) => void
  sx?: any
  initialValue: boolean
}

export default function CheckboxInput({
  label,
  onChange,
  sx,
  initialValue
}: CheckboxInputProp) {
  const [checked, setChecked] = useState(initialValue)

  useEffect(() => {
    setChecked(initialValue)
  }, [initialValue])

  const changeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setChecked(event.target.checked)
      onChange(event.target.checked)
    },
    [onChange],
  )

  return (
    <StyledFormControlLabel
      sx={sx}
      control={<StyledCheckbox checked={checked} onChange={changeHandler} />}
      label={<Text sx={{ ...sx?.text }}>{label}</Text>}
    />
  )
}
